import {
  ArrowLeft,
  Search,
  SlidersHorizontal, // Icon for filters
} from "lucide-react";
import { useState, useEffect, useMemo, useRef } from "react";
import EditStaffModal from "../../components/AdminPortal/EditStaffModel";
import { useNavigate, useParams } from "react-router-dom";
import TopNavbar from "../../components/AdminPortal/TopNavbar";
import { useSidebar } from "../../context/SidebarContext";
import { staffAPI } from "../../services/AdminPortal/staffAPI";
import { loanAPI } from "../../services/AdminPortal/loansAPI";
import { attendanceAPI } from "../../services/AdminPortal/staffworkAPI";
import { taskAPI } from "../../services/AdminPortal/taskAPI"; // Assuming taskAPI is correctly imported
import { affiliateAPI } from "../../services/AdminPortal/affiliateAPI";
import ApplicationsChart from "../../components/AdminPortal/STAFF/charts/ApplicationsChart";
import AssignTask from "../../components/AdminPortal/AssignTask";
import AttendanceTab from "../../components/AdminPortal/STAFF/AttendanceTab";
import JSZip from "jszip";
import * as XLSX from "xlsx";
import { useAuth } from "../../context/AuthContext";
import ProfileHeader from "../../components/AdminPortal/STAFF/ProfileHeader";
import Statistics from "../../components/AdminPortal/STAFF/Statistics";
import ApplicationsTable from "../../components/AdminPortal/STAFF/ApplicationsTable";
import NetworkList from "../../components/AdminPortal/NetworkList";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { notificationAPI } from "../../services/notificationAPI";
import { toast } from "react-toastify";
import "../../datepicker.css";

const ITEMS_PER_PAGE = 5;

const StaffProfile = () => {
  const { sidebarExpanded, isMobile } = useSidebar();
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [applications, setApplications] = useState([]);
  const [staffData, setStaffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("workPerformance");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [sendNoteModalOpen, setSendNoteModalOpen] = useState(false);
  const [workfolioData, setWorkfolioData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [staffTasks, setStaffTasks] = useState([]);
  const [taskCounts, setTaskCounts] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0,
  });
  const [summaryStats, setSummaryStats] = useState({
    totalLoanAmount: 0,
    totalCustomers: 0,
    averageProcessingTime: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedApplicationIds, setSelectedApplicationIds] = useState([]);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  // const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false); // Replaced by isDatePickerOpen
  const datePickerRef = useRef(null); // Changed from filterDropdownRef
  const [statisticsFilter, setStatisticsFilter] = useState("all");
  const [pickerMode, setPickerMode] = useState("date"); // "date" | "month" | "year"
  const [createdStaff, setCreatedStaff] = useState([]);
  const [createdStaffLoading, setCreatedStaffLoading] = useState(false);
  const [createdAffiliatePartners, setCreatedAffiliatePartners] = useState([]);
  const [createdAffiliatePartnersLoading, setCreatedAffiliatePartnersLoading] = useState(false);
  const getPickerLabel = () => {
    if (pickerMode === "month") return "Choose Month";
    if (pickerMode === "year") return "Choose Year";
    return "Choose Dates";
  };
  // Effect to reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedStatus, startDate, endDate]);

  const { user } = useAuth();
  const adminId = user.id;
  const API_DOMAIN = import.meta.env.VITE_CRM_API_URL
    ? new URL(import.meta.env.VITE_CRM_API_URL).origin
    : "https://dashboard.lono.co.in";
  const DEFAULT_PROFILE_PIC_WEB_PATH = "/images/profile_icon.png"; // Web accessible path for default

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const filteredApplications = applications.filter(
    (app) =>
      (selectedStatus === "All" || app.status === selectedStatus) &&
      (!searchTerm ||
        app.applicant?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleChange = (event) => {
    setSelectedMonth(parseInt(event.target.value));
  };

  const handleUpdateStaff = (updatedStaff) => {
    setStaffData((prev) => ({
      ...prev,
      data: { ...prev.data, ...updatedStaff },
    }));
  };

  const getDisplayProfilePicUrl = (picturePath) => {
    const defaultUrl = `${API_DOMAIN}${DEFAULT_PROFILE_PIC_WEB_PATH}`;
    if (!picturePath) return defaultUrl;

    // Handle the specific default format from the model if it's still "./public/..."
    if (picturePath === "./public/images/profile_icon.png") {
      return `${API_DOMAIN}/images/profile_icon.png`;
    }
    // Handle paths already correctly formatted (e.g., from uploads like /uploads/profile_pictures/..)
    if (picturePath.startsWith("/")) {
      return `${API_DOMAIN}${picturePath}`;
    }
    // Fallback for other cases or if a full URL is somehow stored
    if (picturePath.startsWith("http://") || picturePath.startsWith("https://"))
      return picturePath;
    return defaultUrl; // Default fallback
  };

  const sendNotification = async (
    userId,
    note,
    notificationType,
    senderUserId
  ) => {
    console.log("🔍 [StaffProfile] sendNotification called with:", {
      userId,
      note,
      notificationType,
      senderUserId,
    });

    try {
      const notification = await notificationAPI.sendNotification(
        userId,
        note,
        notificationType,
        senderUserId
      );
      console.log("✅ [StaffProfile] sendNotification response:", notification);
      toast.success(`Notification sent: ${note}`);
      console.log("Notification sent:", notification);
    } catch (error) {
      console.error("❌ [StaffProfile] sendNotification error:", error);
      toast.error("Could not send notification.");
    }
  };
  const handleExportData = async () => {
    console.log("Export button clicked");
    try {
      if (!staffData?.data) {
        console.warn("No staff data available to export.");
        return;
      }

      const staffDetails = staffData.data;
      const wb = XLSX.utils.book_new();

      // --- Sheet 1: Staff Details ---
      const staffDataForExport = [
        {
          staffId: staffDetails._id,
          name: staffDetails.name,
          email: staffDetails.email,
          role: roleMapping[staffDetails.role] || staffDetails.role,
          phone: staffDetails.phone,
          // Add other relevant staff details
        },
      ];
      const wsStaff = XLSX.utils.json_to_sheet(staffDataForExport);
      XLSX.utils.book_append_sheet(wb, wsStaff, "Staff Details");

      // --- Sheet 2: Loan Applications ---
      if (applications && applications.length > 0) {
        const applicationsForExport = applications.map((app) => ({
          applicationId: app._id,
          applicant: app.applicant,
          principle: app.principle,
          status: app.status,
          appliedDate: formatDate(app.appliedDate),
          processingTime: app.processingTime,
          // Add other relevant application details
        }));
        const wsLoans = XLSX.utils.json_to_sheet(applicationsForExport);
        XLSX.utils.book_append_sheet(wb, wsLoans, "Loan Applications");
      }

      // --- Sheet 3: Monthly Summary ---
      if (monthlySummary) {
        const monthlySummaryForExport = [
          {
            totalWorkingHours: monthlySummary.totalWorkingHours,
            presentDays: monthlySummary.presentDays,
            absentDays: monthlySummary.absentDays,
            leaveDays: monthlySummary.leaveDays,
            // Add other relevant monthly summary details
          },
        ];
        const wsMonthlySummary = XLSX.utils.json_to_sheet(
          monthlySummaryForExport
        );
        XLSX.utils.book_append_sheet(wb, wsMonthlySummary, "Monthly Summary");
      }

      // --- Sheet 4: Summary Statistics ---
      const summaryStatsForExport = [
        {
          totalLoanAmount: summaryStats.totalLoanAmount,
          totalCustomers: summaryStats.totalCustomers,
          averageProcessingTime: summaryStats.averageProcessingTime,
        },
      ];
      const wsSummaryStats = XLSX.utils.json_to_sheet(summaryStatsForExport);
      XLSX.utils.book_append_sheet(wb, wsSummaryStats, "Summary Statistics");

      // --- Sheet 5: Leaves ---
      if (leaves && leaves.length > 0) {
        const leavesForExport = leaves.map((leave) => ({
          leaveId: leave._id,
          startDate: formatDate(leave.startDate),
          endDate: formatDate(leave.endDate),
          status: leave.status,
          reason: leave.reason,
          // Add other relevant leave details
        }));
        const wsLeaves = XLSX.utils.json_to_sheet(leavesForExport);
        XLSX.utils.book_append_sheet(wb, wsLeaves, "Leaves");
      }

      // --- Sheet 6: Tasks ---
      if (staffTasks && staffTasks.length > 0) {
        const tasksForExport = staffTasks.map((task) => ({
          taskId: task._id,
          title: task.title,
          description: task.description,
          status: task.status,
          dueDate: formatDate(task.dueDate),
          // Add other relevant task details
        }));
        const wsTasks = XLSX.utils.json_to_sheet(tasksForExport);
        XLSX.utils.book_append_sheet(wb, wsTasks, "Tasks");
      }

      // Write the workbook to a binary array
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

      // Create a zip file with the Excel file
      const zip = new JSZip();
      zip.file(
        `${staffDetails.name.replace(/\s+/g, "_").toLowerCase()}_details.xlsx`,
        excelBuffer
      ); // Add Excel file to zip

      // Generate the zip file as a Blob
      const zipBlob = await zip.generateAsync({ type: "blob" });

      // Create a download link and trigger download
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${staffDetails.name
        .replace(/\s+/g, "_")
        .toLowerCase()}_details.zip`; // Download the zip file
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error during data export:", error);
    }
  };

  const months = [
    { value: 0, label: "All Time" },
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const handleViewApplication = (applicationId) => {
    const role = user?.role?.toLowerCase();
    if (["admin", "subadmin"].includes(role)) {
      navigate(`/${role}/loan-applications/approval/${applicationId}`);
    } else {
      console.warn("Unknown role:", role);
    }
  };

  const summarizeApplications = (applications) => {
    if (!Array.isArray(applications))
      return {
        totalLoanAmount: 0,
        totalCustomers: 0,
        averageProcessingTime: 0,
      };

    // Total loan amount (sum of principle)
    const totalLoanAmount = applications.reduce(
      (sum, app) => sum + (app.principle || 0),
      0
    );

    // Unique customers based on user._id
    const uniqueCustomerIds = new Set(
      applications.map((app) => app.user?._id).filter(Boolean)
    );
    const totalCustomers = uniqueCustomerIds.size;

    // Average processing time
    const processingTimes = applications
      .map((app) => app.processingTime)
      .filter((time) => typeof time === "number");
    console.log("Extracted processing times (days):", processingTimes); // Log the values being used

    const totalProcessingTime = processingTimes.reduce((sum, t) => sum + t, 0);
    const averageProcessingTime =
      processingTimes.length > 0
        ? totalProcessingTime / processingTimes.length
        : 0;

    return {
      totalLoanAmount,
      totalCustomers,
      averageProcessingTime: Number(averageProcessingTime.toFixed(1)),
    };
  };

  // Helper function to filter applications by a date range (similar to StaffDashboard)
  const getApplicationsInDateRange = useMemo(() => {
    return (apps, startDate, endDate) => {
      if (!apps || !startDate || !endDate) return [];

      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);

      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      return apps.filter((app) => {
        const appDate = new Date(app.createdAt); // Assuming 'createdAt' is the relevant date
        return !isNaN(appDate.getTime()) && appDate >= start && appDate <= end;
      });
    };
  }, []);

  // Helper function to calculate specific metrics for trend calculation
  const calculateMetricsForTrend = useMemo(() => {
    return (filteredApps) => {
      if (!Array.isArray(filteredApps)) {
        return {
          totalApplications: 0,
          activeApplications: 0,
          totalLoanAmount: 0,
          averageProcessingTime: 0,
        };
      }
      const totalApplications = filteredApps.length;
      const activeApplications = filteredApps.filter(
        (app) => app.status?.toLowerCase() === "processing"
      ).length;

      const summary = summarizeApplications(filteredApps); // Reuse existing summarizeApplications

      return {
        totalApplications,
        activeApplications,
        totalLoanAmount: summary.totalLoanAmount,
        averageProcessingTime: summary.averageProcessingTime,
      };
    };
  }, [summarizeApplications]); // Dependency on summarizeApplications

  // Calculate trend data
  const trendData = useMemo(() => {
    if (!applications || applications.length === 0) {
      return {}; // Return empty object if no applications
    }
    const today = new Date();
    const currentTrendPeriodEndDate = new Date(today);
    const currentTrendPeriodStartDate = new Date(today);
    currentTrendPeriodStartDate.setDate(today.getDate() - 29);

    const previousTrendPeriodEndDate = new Date(currentTrendPeriodStartDate);
    previousTrendPeriodEndDate.setDate(
      currentTrendPeriodStartDate.getDate() - 1
    );
    const previousTrendPeriodStartDate = new Date(previousTrendPeriodEndDate);
    previousTrendPeriodStartDate.setDate(
      previousTrendPeriodEndDate.getDate() - 29
    );

    const appsCurrentPeriod = getApplicationsInDateRange(
      applications,
      currentTrendPeriodStartDate,
      currentTrendPeriodEndDate
    );
    const appsPreviousPeriod = getApplicationsInDateRange(
      applications,
      previousTrendPeriodStartDate,
      previousTrendPeriodEndDate
    );

    const metricsCurrent = calculateMetricsForTrend(appsCurrentPeriod);
    const metricsPrevious = calculateMetricsForTrend(appsPreviousPeriod);

    const calculateIndividualTrend = (current, previous) => {
      if (previous === 0) return current > 0 ? Infinity : 0;
      return ((current - previous) / previous) * 100;
    };

    return {
      totalApplicationsTrend: calculateIndividualTrend(
        metricsCurrent.totalApplications,
        metricsPrevious.totalApplications
      ),
      activeApplicationsTrend: calculateIndividualTrend(
        metricsCurrent.activeApplications,
        metricsPrevious.activeApplications
      ),
      avgProcessingTimeTrend: calculateIndividualTrend(
        metricsCurrent.averageProcessingTime,
        metricsPrevious.averageProcessingTime
      ),
      totalLoanAmountTrend: calculateIndividualTrend(
        metricsCurrent.totalLoanAmount,
        metricsPrevious.totalLoanAmount
      ),
    };
  }, [applications, getApplicationsInDateRange, calculateMetricsForTrend]);

  const chartData = useMemo(() => {
    if (!applications || applications.length === 0) {
      return { monthly: [], yearly: [] };
    }

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const monthlyAggregates = {};
    const yearlyAggregates = {};

    applications.forEach((app) => {
      if (!app.createdAt || !app.principle || !app.user?._id) return;

      const date = new Date(app.createdAt);
      const year = date.getFullYear().toString();
      const month = date.getMonth(); // 0-11
      const monthYearKey = `${year}-${String(month + 1).padStart(2, "0")}`; // YYYY-MM

      // Monthly aggregation
      if (!monthlyAggregates[monthYearKey]) {
        monthlyAggregates[monthYearKey] = {
          month: monthNames[month],
          year: year, // Store year for potential multi-year monthly view if needed
          monthNumeric: month, // For sorting
          totalLoanAmount: 0,
          customerIds: new Set(),
        };
      }
      monthlyAggregates[monthYearKey].totalLoanAmount += app.principle;
      monthlyAggregates[monthYearKey].customerIds.add(app.user._id);

      // Yearly aggregation
      if (!yearlyAggregates[year]) {
        yearlyAggregates[year] = {
          year: year,
          totalLoanAmount: 0,
          customerIds: new Set(),
        };
      }
      yearlyAggregates[year].totalLoanAmount += app.principle;
      yearlyAggregates[year].customerIds.add(app.user._id);
    });

    const dynamicMonthlyData = Object.values(monthlyAggregates)
      .map((data) => ({
        ...data,
        totalCustomers: data.customerIds.size,
        // totalLoanAmount: parseFloat((data.totalLoanAmount / 100000).toFixed(2)) // Convert to lakhs
      }))
      .sort((a, b) => {
        // Sort by year then by month
        if (a.year !== b.year) return parseInt(a.year) - parseInt(b.year);
        return a.monthNumeric - b.monthNumeric;
      })
      .map(({ monthNumeric, customerIds, ...rest }) => rest); // Remove helper fields

    const dynamicYearlyData = Object.values(yearlyAggregates)
      .map((data) => ({
        ...data,
        totalCustomers: data.customerIds.size,
        // totalLoanAmount: parseFloat((data.totalLoanAmount / 100000).toFixed(2)) // Convert to lakhs
      }))
      .sort((a, b) => parseInt(a.year) - parseInt(b.year));

    return { monthly: dynamicMonthlyData, yearly: dynamicYearlyData };
  }, [applications]);

  const finalFilteredApplications = useMemo(() => {
    return applications.filter((app) => {
      const appDate = new Date(app.createdAt);
      const matchesSearch =
        !searchTerm ||
        app.user?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        selectedStatus === "All" || app.status === selectedStatus;
      const matchesDate =
        (!startDate || appDate >= startDate) &&
        (!endDate || appDate <= new Date(endDate.getTime() + 86399999)); // Include end of day

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [applications, searchTerm, selectedStatus, startDate, endDate]);

  const totalPages = Math.ceil(
    finalFilteredApplications.length / ITEMS_PER_PAGE
  );
  const currentTableData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return finalFilteredApplications.slice(
      startIndex,
      startIndex + ITEMS_PER_PAGE
    );
  }, [finalFilteredApplications, currentPage, ITEMS_PER_PAGE]);

  // Handler for selecting/deselecting a single application
  const handleSelectApplication = (applicationId) => {
    setSelectedApplicationIds((prevSelectedIds) =>
      prevSelectedIds.includes(applicationId)
        ? prevSelectedIds.filter((id) => id !== applicationId)
        : [...prevSelectedIds, applicationId]
    );
  };

  // Handler for selecting/deselecting all applications on the current page
  const handleSelectAllApplications = () => {
    const currentPageIds = currentTableData.map((app) => app._id);
    if (
      selectedApplicationIds.length === currentPageIds.length &&
      currentPageIds.every((id) => selectedApplicationIds.includes(id))
    ) {
      // If all on current page are selected, deselect them
      setSelectedApplicationIds((prevSelectedIds) =>
        prevSelectedIds.filter((id) => !currentPageIds.includes(id))
      );
    } else {
      // Otherwise, select all on current page (add those not already selected)
      setSelectedApplicationIds((prevSelectedIds) => [
        ...new Set([...prevSelectedIds, ...currentPageIds]),
      ]);
    }
  };
  const isAllCurrentPageApplicationsSelected =
    currentTableData.length > 0 &&
    currentTableData.every((app) => selectedApplicationIds.includes(app._id));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch staff details
        const staffResponse = await staffAPI.getStaffById({ staffId: id });
        console.log("Staff Response:", staffResponse);
        setStaffData(staffResponse);

        // Fetch assigned applications
        const applicationsResponse = await loanAPI.getLoansByStaffId(id);
        const appData = applicationsResponse.data;
        console.log("this is response from the application ", appData);
        setApplications(appData);

        // 🔢 Calculate application summary after setting applications
        const { totalLoanAmount, totalCustomers, averageProcessingTime } =
          summarizeApplications(appData, selectedYear, selectedMonth || 0);
        setSummaryStats({
          totalLoanAmount,
          totalCustomers,
          averageProcessingTime: averageProcessingTime.toFixed(1),
        });

        // Fetch workfolio data for the selected year and month
        const workfolioParams = {};
        if (selectedYear) {
          workfolioParams.year = selectedYear;
        }
        if (selectedMonth && selectedMonth !== 0) {
          workfolioParams.month = selectedMonth;
        }
        const workfolioResponse = await attendanceAPI.getWorkfolio(
          id,
          workfolioParams.year,
          workfolioParams.month
        );
        console.log("Workfolio API Response:", workfolioResponse);
        setWorkfolioData(workfolioResponse);
        const summaryData = workfolioResponse?.monthlySummary || null;
        setMonthlySummary(summaryData);

        // Fetch leaves
        const leavesResponse = await attendanceAPI.getStaffLeaves(id);
        console.log("Leaves Response:", leavesResponse);
        setLeaves(leavesResponse);

        // Fetch tasks
        if (id) {
          const tasksApiResponse = await taskAPI.getStaffTasks(id);
          const tasks = tasksApiResponse.data?.tasks || [];
          setStaffTasks(tasks);

          const counts = tasks.reduce(
            (acc, task) => {
              const status = task.status?.toLowerCase();
              if (status === "pending") acc.pending++;
              else if (status === "in_progress" || status === "in progress")
                acc.inProgress++;
              else if (status === "completed") acc.completed++;
              return acc;
            },
            { pending: 0, inProgress: 0, completed: 0 }
          );
          setTaskCounts(counts);
        } else {
          console.warn("Staff ID not available, cannot fetch tasks.");
          setStaffTasks([]);
          setTaskCounts({ pending: 0, inProgress: 0, completed: 0 });
        }

        // Fetch created staff
        if (staffResponse.success && staffResponse.data?._id) {
          try {
            setCreatedStaffLoading(true);
            const response = await staffAPI.getStaffCreatedBy(staffResponse.data._id);
            if (response.success) {
              setCreatedStaff(response.data || []);
            }
          } catch (error) {
            console.error("Error fetching created staff:", error);
          } finally {
            setCreatedStaffLoading(false);
          }
        }

        // Fetch created affiliate partners
        if (staffResponse.success && staffResponse.data?._id) {
          try {
            setCreatedAffiliatePartnersLoading(true);
            console.log("Fetching affiliate partners for staff ID:", staffResponse.data._id);
            console.log("Expected createdBy ID from affiliate partner:", "68c79d5c68f13cb5d58760f6");
            console.log("Do they match?", staffResponse.data._id === "68c79d5c68f13cb5d58760f6");
            const response = await affiliateAPI.getAffiliatePartnersCreatedBy(staffResponse.data._id);
            console.log("Affiliate partners response:", response);
            if (response.success) {
              setCreatedAffiliatePartners(response.data || []);
            }
          } catch (error) {
            console.error("Error fetching created affiliate partners:", error);
          } finally {
            setCreatedAffiliatePartnersLoading(false);
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message || "Failed to fetch data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, selectedYear, selectedMonth]);

  console.log("these are leaves ", leaves); // Log raw response

  console.log("this is application data ", applications); // Log raw response

  // Add a separate useEffect to log the state *after* it updates
  useEffect(() => {
    console.log("Monthly Summary State Updated:", monthlySummary);
  }, [monthlySummary]);

  // Close DatePicker on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target) &&
        !event.target.closest(".react-datepicker__month-container")
      ) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const calculateOverallAttendanceRate = () => {
    if (workfolioData && workfolioData.attendanceFromCreation) {
      const totalRecords = workfolioData.attendanceFromCreation.length;
      const presentRecords = workfolioData.attendanceFromCreation.filter(
        (record) =>
          record.status === "Present" ||
          record.status === "Late" ||
          record.status === "Present (Leave)" // Adjust statuses as needed
      ).length;

      console.log("Total Records:", totalRecords);
      console.log("Present Records:", presentRecords);

      if (totalRecords > 0) {
        const attendanceRate = (presentRecords / totalRecords) * 100;
        return parseFloat(attendanceRate.toFixed(1));
      }
    }
    return 0;
  };

  const roleMapping = {
    admin: "Admin",
    loanOfficer: "Loan Officer",
    subAdmin: "Sub Admin",
    hr: "HR",
    "credit-manager": "Credit Manager",
    marketing: "Marketing",
    sales: "Sales",
    strategy: "Strategy",
  };

  const handleBack = () => {
    const role = user?.role?.toLowerCase();
    if (["admin", "subadmin"].includes(role)) {
      navigate(`/${role}/staff`);
    } else {
      console.warn("Unknown role:", role);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#131313] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500">
        <p>Error: {error}</p>
        <button onClick={handleBack} className="text-[#FFCA20] underline">
          Go Back
        </button>
      </div>
    );
  }

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.error("Invalid date encountered:", dateString); // Log the problematic date
        return "Invalid Date"; // Return a placeholder
      }
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return `${monthNames[date.getMonth()]} ${date.getDate()}`;
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return "Error Date"; // Return a placeholder on error
    }
  };

  const countApplicationsByStatus = (applications) => {
    if (!applications || applications.length === 0) return {}; // Early return if no data

    return applications.reduce((counts, app) => {
      const appStatus = app.status.toLowerCase();
      if (
        [
          "new-leads",
          "approved",
          "rejected",
          "processing",
          "submitted",
          "in-review",
        ].includes(appStatus)
      ) {
        counts[appStatus] = (counts[appStatus] || 0) + 1;
      }

      return counts;
    }, {});
  };

  // Filter applications based on statistics filter
  const getFilteredApplicationsForStats = () => {
    if (statisticsFilter === "all") return applications;

    const now = new Date();
    let startDate;

    switch (statisticsFilter) {
      case "monthly":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "quarterly":
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        break;
      case "yearly":
        startDate = new Date(
          now.getFullYear() - 1,
          now.getMonth(),
          now.getDate()
        );
        break;
      default:
        return applications;
    }

    return applications.filter((app) => {
      if (!app.createdAt) return false;
      return new Date(app.createdAt) >= startDate;
    });
  };

  const filteredApplicationsForStats = getFilteredApplicationsForStats();
  const statusCounts = countApplicationsByStatus(filteredApplicationsForStats);
  const totalApplications = filteredApplicationsForStats.length || 0;

  // Handle statistics filter change
  const handleStatisticsFilterChange = (newFilter) => {
    setStatisticsFilter(newFilter);
  };

  return (
    <div
      className="bg-black text-white mt-12"
      style={{
        marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
        width: isMobile
          ? "100%"
          : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
      }}
    >
      <TopNavbar />

      <div className="p-4">
        <div className="flex items-center mb-6">
          <button
            onClick={() => handleBack()}
            className="text-[#FFCA20] hover:underline flex items-center mr-2"
          >
            Staff Management
          </button>{" "}
          {"  > Staff Profile"}
        </div>

        {/* Profile Header */}
        <ProfileHeader
          staffData={staffData}
          getDisplayProfilePicUrl={getDisplayProfilePicUrl}
          roleMapping={roleMapping}
          onEditProfile={() => setIsEditModalOpen(true)}
          onExportData={handleExportData}
          onAssignTask={() => setSendNoteModalOpen(true)}
        />
        <EditStaffModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          staffData={staffData?.data}
          onUpdate={handleUpdateStaff}
        />
        <AssignTask
          adminId={adminId}
          sendNotification={sendNotification}
          isOpen={sendNoteModalOpen}
          onClose={() => setSendNoteModalOpen(false)}
          staffData={staffData?.data}
        />

        {/* Tabs */}
        <div className="tab-strip-border overflow-x-auto mb-2 scrollbar-hide">
          <div className="flex">
            <button
              className={`tab-button ${
                activeTab === "workPerformance"
                  ? "active-tab-border" // Apply the active border class
                  : "inactive-tab" // Apply inactive tab class
              }`}
              onClick={() => setActiveTab("workPerformance")}
            >
              Work Performance
            </button>
            <button
              className={`tab-button ${
                activeTab === "attendance"
                  ? "active-tab-border" // Apply the active border class
                  : "inactive-tab" // Apply inactive tab class
              }`}
              onClick={() => setActiveTab("attendance")}
            >
              Attendance & Task Management
            </button>
            <button
              className={`tab-button ${
                activeTab === "network-list"
                  ? "active-tab-border" // Apply the active border class
                  : "inactive-tab" // Apply inactive tab class
              }`}
              onClick={() => setActiveTab("network-list")}
            >
              Network List
            </button>
          
            <button
              className={`tab-button ${
                activeTab === "affiliateList"
                  ? "active-tab-border"
                  : "inactive-tab"
              }`}
              onClick={() => setActiveTab("affiliateList")}
            >
              Affiliate List
            </button>
          </div>
        </div>
        {/* Render different sections based on activeTab */}
        {activeTab === "workPerformance" ? (
          <div>
            <Statistics
              totalApplications={totalApplications}
              statusCounts={statusCounts}
              summaryStats={summarizeApplications(filteredApplicationsForStats)}
              trendData={trendData}
              onFilterChange={handleStatisticsFilterChange}
            />

            {/* Charts */}
            <div className="flex flex-col lg:flex-row w-full flex-1 items-stretch gap-4 mb-6">
              {/* Lead Conversion Chart */}
              <div className="bg-[#131313] p-4 rounded w-full lg:w-[30%]">
                <div className="flex flex-row justify-between mb-4">
                  <div className="text-xs uppercase whitespace-nowrap">
                    lead conversion
                  </div>
                  {/* month selector */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setIsMonthDropdownOpen(!isMonthDropdownOpen)
                      }
                      className="w-[100px] rounded bg-transparent border border-[#333333] text-white px-2 py-1 flex justify-between items-center"
                    >
                      <span>
                        {months.find((m) => m.value === selectedMonth)?.label}
                      </span>
                      <svg
                        className={`w-4 h-4 transition-transform ${
                          isMonthDropdownOpen ? "rotate-180" : ""
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    {isMonthDropdownOpen && (
                      <div className="absolute right-0 z-10 mt-1 w-[100px] bg-[#131313] border border-[#333333] rounded max-h-32 overflow-y-auto">
                        {months.map(({ value, label }) => (
                          <div
                            key={value}
                            onClick={() => {
                              setSelectedMonth(value);
                              setIsMonthDropdownOpen(false);
                            }}
                            className="px-2 py-1 text-white hover:bg-[#333333] cursor-pointer text-sm"
                          >
                            {label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm">Total Leads</div>
                  <div className="text-2xl font-bold">{totalApplications}</div>
                  <div className="text-sm">Converted Leads</div>
                  <div className="text-2xl font-bold text-[#FFCA20] ">
                    {statusCounts["approved"] || 0}
                  </div>
                  <div className="text-sm">Lead Conversion Percentage</div>
                  <div className="text-2xl font-bold text-[#FFCA20] ">
                    {totalApplications > 0
                      ? (
                          ((statusCounts["approved"] || 0) /
                            totalApplications) *
                          100
                        ).toFixed(2)
                      : "0.00"}
                    %
                  </div>
                </div>
              </div>

              {/* Total Loan Amount Chart */}
              <div className="w-full lg:w-[calc(70%-2rem)] xl:w-[calc(70%-4rem)] bg-[#131313] rounded p-2 overflow-x-auto">
                <div className="min-w-[600px]">
                  <ApplicationsChart
                    monthlyData={chartData.monthly}
                    yearlyData={chartData.yearly}
                  />
                </div>
              </div>

              {/* Yearly Data Chart */}
              <div className="bg-[#131313] p-4 rounded w-full lg:w-[20%] xl:w-[calc(30%-2rem)]">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-md uppercase">Yearly data</div>
                </div>
                <div>
                  <div className="text-sm">Total Amount</div>
                  <div className="text-2xl font-bold text-[#FFCA20] ">
                    {summaryStats.totalLoanAmount}
                  </div>
                  <div className="text-sm">Total Customers</div>
                  <div className="text-2xl font-bold text-[#800080] ">
                    {summaryStats.totalCustomers}
                  </div>
                </div>
              </div>
            </div>

            {/* Current Applications Table */}
            <h3 className="text-lg font-semibold p-2">Current Applications</h3>
            <div className="flex flex-col gap-4 mb-4 relative">
              <div className="flex flex-col   lg:items-center gap-4">
                <div className="relative w-full lg:max-w-xs text-white">
                  <div className="absolute left-3 top-2.5 text-gray-400">
                    <Search size={16} />
                  </div>
                  <input
                    type="text"
                    placeholder="Search Applications"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-black rounded-3xl px-10 py-2 text-sm w-full border border-[#333333]"
                  />
                </div>

                <div className="relative bg-transparent rounded-lg border-2 border-[#333333]">
                  <button
                    className="flex items-center px-3 py-2 focus:outline-none gap-2"
                    onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                  >
                    <svg
                      width="14"
                      height="16"
                      viewBox="0 0 14 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0 14.5V6H14V14.5C14 15.3438 13.3125 16 12.5 16H1.5C0.65625 16 0 15.3438 0 14.5ZM10 8.5V9.5C10 9.78125 10.2188 10 10.5 10H11.5C11.75 10 12 9.78125 12 9.5V8.5C12 8.25 11.75 8 11.5 8H10.5C10.2188 8 10 8.25 10 8.5ZM10 12.5V13.5C10 13.7812 10.2188 14 10.5 14H11.5C11.75 14 12 13.7812 12 13.5V12.5C12 12.25 11.75 12 11.5 12H10.5C10.21875 12 10 12.25 10 12.5ZM6 8.5V9.5C6 9.78125 6.21875 10 6.5 10H7.5C7.75 10 8 9.78125 8 9.5V8.5C8 8.25 7.75 8 7.5 8H6.5C6.21875 8 6 8.25 6 8.5ZM6 12.5V13.5C6 13.7812 6.21875 14 6.5 14H7.5C7.75 14 8 13.7812 8 13.5V12.5C8 12.25 7.75 12 7.5 12H6.5C6.21875 12 6 12.25 6 12.5ZM2 8.5V9.5C2 9.78125 2.21875 10 2.5 10H3.5C3.75 10 4 9.78125 4 9.5V8.5C4 8.25 3.75 8 3.5 8H2.5C2.21875 8 2 8.25 2 8.5ZM2 12.5V13.5C2 13.7812 2.21875 14 2.5 14H3.5C3.75 14 4 13.7812 4 13.5V12.5C4 12.25 3.75 12 3.5 12H2.5C2.21875 12 2 12.25 2 12.5ZM12.5 2C13.3125 2 14 2.6875 14 3.5V5H0V3.5C0 2.6875 0.65625 2 1.5 2H3V1C3 0.46875 3.4375 0 4 0C4.53125 0 5 0.46875 5 1V2H9V1C9 0.46875 9.4375 0 10 0C10.53125 0 11 0.46875 11 1V2H12.5Z"
                        fill="#FFCA20"
                      />
                    </svg>
                    {startDate && endDate
                      ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                      : getPickerLabel()}
                  </button>

                  {isDatePickerOpen && (
                    <div className="absolute z-10 mt-2 bg-[#1e1e1e] rounded-md p-3">
                      {/* Picker Mode Switch */}
                      <div className="flex gap-2 mb-2">
                        <button
                          className={`px-3 py-1 rounded ${
                            pickerMode === "date"
                              ? "bg-yellow-500 text-black"
                              : "bg-[#333] text-white"
                          }`}
                          onClick={() => setPickerMode("date")}
                        >
                          Date
                        </button>
                        <button
                          className={`px-3 py-1 rounded ${
                            pickerMode === "month"
                              ? "bg-yellow-500 text-black"
                              : "bg-[#333] text-white"
                          }`}
                          onClick={() => setPickerMode("month")}
                        >
                          Month
                        </button>
                        <button
                          className={`px-3 py-1 rounded ${
                            pickerMode === "year"
                              ? "bg-yellow-500 text-black"
                              : "bg-[#333] text-white"
                          }`}
                          onClick={() => setPickerMode("year")}
                        >
                          Year
                        </button>
                      </div>

                      {/* Calendar */}
                      <DatePicker
                        selected={startDate}
                        onChange={(update) => {
                          if (pickerMode === "date") {
                            setDateRange(update);
                            if (update[0] && update[1])
                              setIsDatePickerOpen(false);
                          } else {
                            setDateRange([update, update]);
                            setIsDatePickerOpen(false);
                          }
                        }}
                        startDate={startDate}
                        endDate={endDate}
                        selectsRange={pickerMode === "date"}
                        showMonthYearPicker={pickerMode === "month"}
                        showYearPicker={pickerMode === "year"}
                        inline
                      />
                    </div>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-500 lg:hidden">
                Select to enable bulk options{" "}
                <span className="ml-2 text-sm text-blue-400">
                  ({selectedApplicationIds.length} selected)
                </span>
              </p>
            </div>

            <ApplicationsTable
              onViewApplication={handleViewApplication}
              currentTableData={currentTableData}
              totalPages={totalPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              selectedApplicationIds={selectedApplicationIds}
              onSelectApplication={handleSelectApplication}
              onSelectAllApplications={handleSelectAllApplications}
              isAllSelected={isAllCurrentPageApplicationsSelected}
            />
          </div>
        ) : activeTab === "attendance" ? (
          <AttendanceTab
            sendNotification={sendNotification}
            adminId={adminId}
            monthlySummary={monthlySummary}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            handleChange={handleChange}
            months={months}
            taskCounts={taskCounts}
            staffTasks={staffTasks}
            staffData={staffData}
            leaves={leaves}
            setLeaves={setLeaves}
            performance={workfolioData?.attendance || []}
            attendanceAPI={attendanceAPI}
            formatDate={formatDate}
          />
        ) : activeTab === "network-list" ? (
          <NetworkList Id={staffData?.data?._id} />
        ) : activeTab === "staffCreated" ? (
          <div className="bg-[#131313] rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4">Staff Created</h3>
            {createdStaffLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
              </div>
            ) : createdStaff.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-[#333333]">
                      <th className="text-left p-3 text-sm font-medium text-gray-400">Name</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-400">Staff ID</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-400">Role</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-400">Email</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-400">Created Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {createdStaff.map((staff) => (
                      <tr key={staff._id} className="border-b border-[#333333] hover:bg-[#1a1a1a]">
                        <td className="p-3 text-sm">{staff.name}</td>
                        <td className="p-3 text-sm">{staff.staffId || staff._id}</td>
                        <td className="p-3 text-sm">{roleMapping[staff.role] || staff.role}</td>
                        <td className="p-3 text-sm">{staff.email}</td>
                        <td className="p-3 text-sm">
                          {staff.createdAt ? new Date(staff.createdAt).toLocaleDateString('en-GB') : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p>No staff members created by this user yet.</p>
              </div>
            )}
          </div>
        ) : activeTab === "affiliateList" ? (
          <div className="bg-[#131313] rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4">Affiliate Partners Created</h3>
            {createdAffiliatePartnersLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
              </div>
            ) : createdAffiliatePartners.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-[#333333]">
                      <th className="text-left p-3 text-sm font-medium text-gray-400">Name</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-400">Partner ID</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-400">Phone</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-400">Email</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-400">Category</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-400">Status</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-400">Created Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {createdAffiliatePartners.map((partner) => (
                      <tr key={partner._id} className="border-b border-[#333333] hover:bg-[#1a1a1a]">
                        <td className="p-3 text-sm">{partner.name}</td>
                        <td className="p-3 text-sm">{partner.partnerId}</td>
                        <td className="p-3 text-sm">{partner.phoneNumber}</td>
                        <td className="p-3 text-sm">{partner.emailId}</td>
                        <td className="p-3 text-sm">{partner.affiliateCategory}</td>
                        <td className="p-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            partner.status === 'active' ? 'bg-green-500/20 text-green-400' :
                            partner.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {partner.status}
                          </span>
                        </td>
                        <td className="p-3 text-sm">
                          {partner.createdAt || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p>No affiliate partners created by this staff member yet.</p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default StaffProfile;
